import React, { useState, useMemo, useEffect } from "react"
import PropTypes from "prop-types"
import {
  formatCurrency, formatNumber, formatPercent,
  familiaConfig, descargarCSV,
} from "../helpers"

const PAGE_SIZE_OPTS = [25, 50, 100, 9999]
const ORDEN_OPTS = [
  { value: "valor_desc",     label: "↓ Mayor impuesto" },
  { value: "valor_asc",      label: "↑ Menor impuesto" },
  { value: "base_desc",      label: "↓ Mayor base" },
  { value: "base_asc",       label: "↑ Menor base" },
  { value: "excluido_desc",  label: "↓ Mayor % excluido" },
  { value: "lineas_desc",    label: "↓ Más líneas" },
  { value: "nombre_asc",     label: "A-Z nombre" },
]

const useDebounced = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

const SucursalesTab = ({ data, onAbrirDetalle }) => {
  const [busqueda, setBusqueda] = useState("")
  const busquedaDebounced = useDebounced(busqueda, 300)
  const [filtroFamilia, setFiltroFamilia] = useState("")
  const [orden, setOrden] = useState("valor_desc")
  const [pageSize, setPageSize] = useState(50)
  const [page, setPage] = useState(0)
  const [soloAlertas, setSoloAlertas] = useState(false)

  const sucursales = useMemo(() => data?.sucursales || [], [data])
  const familias = data?.por_familia || []

  // Procesa: aplica filtros + alertas + orden
  const sucursalesProcesadas = useMemo(() => {
    let lista = sucursales.map(s => {
      const tot = s.totales_sucursal || {}
      const impuestos = s.impuestos || []
      const familiasSucursal = new Set(impuestos.map(i => i.familia))
      // Excluidos = bloque nuevo (con fallback al viejo `exentos` por compat)
      const excluidos = s.excluidos ?? s.exentos ?? {}
      // Datos derivados (back nuevo trae total_excluidos / porcentaje_excluido en totales_sucursal,
      // si no, derivamos del bloque excluidos/exentos)
      const totalExcluidoSuc = tot.total_excluidos ?? tot.total_exentos ?? excluidos.importe ?? excluidos.base_gravable ?? 0
      const pctExcluidoSuc = tot.porcentaje_excluido ?? tot.porcentaje_exento ?? 0
      // Alertas heurísticas
      const alertas = []
      if (Number(pctExcluidoSuc) >= 5) alertas.push({ tipo: "excluido_alto", desc: `${formatPercent(pctExcluidoSuc, 1)} excluido` })
      if (impuestos.length === 0) alertas.push({ tipo: "sin_impuestos", desc: "Sin impuestos registrados" })
      return {
        sucursal: s.sucursal,
        impuestos,
        excluidos,
        totales: tot,
        totalExcluidoSuc,
        pctExcluidoSuc,
        familias: Array.from(familiasSucursal),
        alertas,
      }
    })

    // Filtro por familia
    if (filtroFamilia) {
      lista = lista.filter(s => s.familias.includes(filtroFamilia))
    }
    // Filtro por búsqueda
    const q = (busquedaDebounced || "").trim().toLowerCase()
    if (q) {
      lista = lista.filter(s => s.sucursal.toLowerCase().includes(q))
    }
    // Solo alertas
    if (soloAlertas) {
      lista = lista.filter(s => s.alertas.length > 0)
    }
    // Orden
    const cmp = {
      valor_desc:  (a, b) => (b.totales.total_impuestos || 0) - (a.totales.total_impuestos || 0),
      valor_asc:   (a, b) => (a.totales.total_impuestos || 0) - (b.totales.total_impuestos || 0),
      base_desc:   (a, b) => (b.totales.total_base_gravable || 0) - (a.totales.total_base_gravable || 0),
      base_asc:    (a, b) => (a.totales.total_base_gravable || 0) - (b.totales.total_base_gravable || 0),
      excluido_desc: (a, b) => (b.pctExcluidoSuc || 0) - (a.pctExcluidoSuc || 0),
      lineas_desc: (a, b) => (b.totales.total_lineas || 0) - (a.totales.total_lineas || 0),
      nombre_asc:  (a, b) => (a.sucursal || "").localeCompare(b.sucursal || ""),
    }[orden] || ((a, b) => 0)
    return [...lista].sort(cmp)
  }, [sucursales, filtroFamilia, busquedaDebounced, soloAlertas, orden])

  // Paginación local
  const totalPages = Math.max(1, Math.ceil(sucursalesProcesadas.length / pageSize))
  const offset = page * pageSize
  const pagina = sucursalesProcesadas.slice(offset, offset + pageSize)

  useEffect(() => { setPage(0) }, [filtroFamilia, busquedaDebounced, soloAlertas, orden, pageSize])

  // Conteo de alertas
  const totalAlertas = sucursalesProcesadas.reduce((acc, s) => acc + s.alertas.length, 0)

  // Export CSV
  const handleExportCSV = () => {
    const headers = ["Sucursal", "Líneas", "Base gravable", "Total impuestos", "Total con impuestos", "Excluidos", "% Gravado", "% Excluido"]
    const rows = sucursalesProcesadas.map(s => [
      s.sucursal,
      s.totales.total_lineas || 0,
      s.totales.total_base_gravable || 0,
      s.totales.total_impuestos || 0,
      s.totales.total_con_impuestos || 0,
      s.totalExcluidoSuc || 0,
      formatPercent(s.totales.porcentaje_gravado, 2),
      formatPercent(s.pctExcluidoSuc, 2),
    ])
    descargarCSV(`impuestos_sucursales_${new Date().toISOString().split("T")[0]}.csv`, headers, rows)
  }

  if (!data || sucursales.length === 0) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
        <i className="mdi mdi-source-branch-remove" style={{ fontSize: 48, color: "#cbd5e1" }} />
        <div className="mt-2">No hay sucursales en el período.</div>
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Toolbar de filtros */}
      <div style={{
        background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12,
        padding: "12px 14px",
        display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
      }}>
        <div style={{ flex: "1 1 220px", minWidth: 180 }}>
          <input
            type="text"
            placeholder="🔎 Buscar sucursal…"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            style={{
              width: "100%", fontSize: 13, padding: "6px 12px",
              borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", height: 34,
            }}
          />
        </div>

        <select value={filtroFamilia} onChange={e => setFiltroFamilia(e.target.value)}
          style={{ fontSize: 13, padding: "6px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", height: 34 }}>
          <option value="">Todas las familias</option>
          {familias.map(f => <option key={f.familia} value={f.familia}>{f.familia}</option>)}
        </select>

        <select value={orden} onChange={e => setOrden(e.target.value)}
          style={{ fontSize: 13, padding: "6px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", height: 34 }}>
          {ORDEN_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <label style={{
          display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600,
          background: soloAlertas ? "#fef3c7" : "#f1f5f9",
          color: soloAlertas ? "#92400e" : "#475569",
          padding: "6px 12px", borderRadius: 6, height: 34, cursor: "pointer", userSelect: "none",
          border: soloAlertas ? "1px solid #fde68a" : "1px solid transparent",
        }}>
          <input type="checkbox" checked={soloAlertas} onChange={e => setSoloAlertas(e.target.checked)}
            style={{ accentColor: "#f59e0b" }} />
          <i className="mdi mdi-alert-circle-outline" />
          Solo con alertas {totalAlertas > 0 && <span style={{ fontWeight: 700 }}>· {totalAlertas}</span>}
        </label>

        <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
          style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "1px solid #cbd5e1", background: "#fff", height: 34 }}>
          {PAGE_SIZE_OPTS.map(s => <option key={s} value={s}>{s === 9999 ? "Todas" : `${s} por pág`}</option>)}
        </select>

        <button type="button" className="btn btn-sm btn-outline-success ms-auto"
          onClick={handleExportCSV} disabled={sucursalesProcesadas.length === 0}>
          <i className="mdi mdi-file-delimited me-1" />
          CSV
        </button>
      </div>

      {/* Tabla */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden" }}>
        <div style={{ overflowX: "auto", maxHeight: "70vh" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
            <thead>
              <tr>
                <th style={th}>Sucursal</th>
                <th style={{ ...th, textAlign: "center" }}>Familias</th>
                <th style={{ ...th, textAlign: "right" }}>Líneas</th>
                <th style={{ ...th, textAlign: "right" }}>Base gravable</th>
                <th style={{ ...th, textAlign: "right" }}>Impuestos</th>
                <th style={{ ...th, textAlign: "right" }}>Con impuestos</th>
                <th style={{ ...th, textAlign: "right" }}>Excluidos</th>
                <th style={{ ...th, textAlign: "center" }}>% Gravado</th>
                <th style={{ ...th, textAlign: "center" }}>% Excluido</th>
                <th style={{ ...th, textAlign: "center" }}>Alertas</th>
                <th style={{ ...th, textAlign: "center", width: 80 }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {pagina.map((s, i) => (
                <tr key={`${s.sucursal}-${i}`} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                  <td style={td}>
                    <strong style={{ color: "#0f172a" }}>{s.sucursal}</strong>
                    <div style={{ fontSize: 10, color: "#94a3b8" }}>
                      {s.impuestos.length} {s.impuestos.length === 1 ? "tipo" : "tipos"}
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    <div style={{ display: "inline-flex", flexWrap: "wrap", gap: 3, justifyContent: "center" }}>
                      {s.familias.map(f => {
                        const cfg = familiaConfig(f)
                        return (
                          <span key={f} style={{
                            padding: "1px 6px", borderRadius: 4, fontSize: 9, fontWeight: 700,
                            background: cfg.bg, color: cfg.color,
                          }}>{f}</span>
                        )
                      })}
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    {formatNumber(s.totales.total_lineas)}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    {formatCurrency(s.totales.total_base_gravable)}
                  </td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 600, color: "#10b981" }}>
                    {formatCurrency(s.totales.total_impuestos)}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    {formatCurrency(s.totales.total_con_impuestos)}
                  </td>
                  <td style={{ ...td, textAlign: "right", color: "#f59e0b" }}>
                    {formatCurrency(s.totalExcluidoSuc)}
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <div style={{ width: 50, height: 5, background: "#e2e8f0", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{
                          width: `${Math.min(100, s.totales.porcentaje_gravado)}%`,
                          height: "100%", background: "#3b82f6",
                        }} />
                      </div>
                      <strong style={{ fontSize: 11 }}>{formatPercent(s.totales.porcentaje_gravado, 1)}</strong>
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    {s.pctExcluidoSuc > 0 ? (
                      <span style={{
                        padding: "1px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                        background: s.pctExcluidoSuc >= 5 ? "#fef3c7" : "#f1f5f9",
                        color:      s.pctExcluidoSuc >= 5 ? "#92400e" : "#475569",
                      }}>
                        {formatPercent(s.pctExcluidoSuc, 2)}
                      </span>
                    ) : <span style={{ color: "#cbd5e1" }}>—</span>}
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    {s.alertas.length === 0 ? (
                      <span style={{ color: "#cbd5e1" }}>—</span>
                    ) : (
                      <div style={{ display: "inline-flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
                        {s.alertas.map((a, idx) => (
                          <span key={idx} title={a.desc} style={{
                            padding: "1px 6px", borderRadius: 4, fontSize: 9, fontWeight: 700,
                            background: "#fef3c7", color: "#92400e",
                          }}>
                            <i className="mdi mdi-alert-circle me-1" />
                            {a.tipo}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    <button type="button" className="btn btn-sm btn-outline-primary"
                      onClick={() => onAbrirDetalle(s)} title="Ver detalle">
                      <i className="mdi mdi-eye-outline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        <div style={{
          padding: "10px 16px", borderTop: "1px solid #e2e8f0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 8, fontSize: 13, color: "#475569",
        }}>
          <span>
            Mostrando {sucursalesProcesadas.length === 0 ? 0 : offset + 1}—
            {Math.min(offset + pagina.length, sucursalesProcesadas.length)} de {sucursalesProcesadas.length}
            {sucursalesProcesadas.length !== sucursales.length && (
              <span style={{ color: "#94a3b8" }}> (filtrado de {sucursales.length})</span>
            )}
          </span>
          {totalPages > 1 && (
            <div style={{ display: "flex", gap: 4 }}>
              <button type="button" className="btn btn-sm btn-outline-secondary"
                disabled={page === 0} onClick={() => setPage(0)}>
                <i className="mdi mdi-chevron-double-left" />
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary"
                disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
                <i className="mdi mdi-chevron-left" />
              </button>
              <span style={{ padding: "0 10px", display: "inline-flex", alignItems: "center", fontWeight: 600 }}>
                {page + 1} / {totalPages}
              </span>
              <button type="button" className="btn btn-sm btn-outline-secondary"
                disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                <i className="mdi mdi-chevron-right" />
              </button>
              <button type="button" className="btn btn-sm btn-outline-secondary"
                disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>
                <i className="mdi mdi-chevron-double-right" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const th = {
  background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
  padding: "10px 12px", textAlign: "left",
  fontSize: 11, color: "#475569", fontWeight: 700,
  textTransform: "uppercase", letterSpacing: 0.4, position: "sticky", top: 0, zIndex: 1,
}
const td = {
  padding: "10px 12px", borderBottom: "1px solid #f1f5f9",
  fontSize: 13, color: "#0f172a", verticalAlign: "middle",
}

SucursalesTab.propTypes = {
  data: PropTypes.object,
  onAbrirDetalle: PropTypes.func.isRequired,
}

export default SucursalesTab
